// src/utils/registrationDeadline.ts
//
// Uzávěrka přihlášek – jedna definice pro přihlášku (PaymentRace), přehled
// střelce (UserDashboard) a pravidla Firestore (registrationClosesAtOf,
// bezpečnost 2): uzávěrka z formuláře závodu, jinak začátek dne závodu
// v Praze. Dřív klient bral půlnoc v čase zařízení – na zařízení mimo český
// čas pak ukázal přihlášku, kterou pravidla zamítnou (nebo naopak).

import { toDateSafe } from "./pricing";

/** Datum závodu z formuláře ("YYYY-MM-DD"; pravidla berou i měsíc a den bez nuly). */
const ISO_DAY = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;

/**
 * Půlnoc dne y-m-d v Praze jako okamžik: SEČ (UTC+1), nebo letní čas
 * (UTC+2) – ze dvou kandidátů ten, který Intl s časovou zónou Europe/Prague
 * ukáže jako 00:00 téhož dne (v noc přechodu má půlnoc ještě předchozí
 * posun, stejně jako pravidla). Prohlížeč bez časových zón → místní půlnoc
 * (dřívější chování).
 */
export function pragueMidnight(year: number, month: number, day: number): Date {
  try {
    const fmt = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/Prague",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    for (const hours of [1, 2]) {
      const t = new Date(Date.UTC(year, month - 1, day) - hours * 3600 * 1000);
      const parts: Record<string, string> = {};
      fmt.formatToParts(t).forEach((p) => {
        parts[p.type] = p.value;
      });
      if (
        Number(parts.year) === year &&
        Number(parts.month) === month &&
        Number(parts.day) === day &&
        Number(parts.hour) % 24 === 0 &&
        Number(parts.minute) === 0
      ) {
        return t;
      }
    }
  } catch {
    // Bez podpory časových zón – níže místní půlnoc.
  }
  return new Date(year, month - 1, day);
}

/** Minimální tvar závodu pro uzávěrku. */
export interface DeadlineRace {
  /** "YYYY-MM-DD", starší závody Firestore Timestamp. */
  date?: unknown;
  /** Timestamp z formuláře závodu (může chybět). */
  registrationClosesAt?: unknown;
}

/**
 * Uzávěrka přihlášek: registrationClosesAt z DB, jinak začátek dne závodu
 * v Praze (datum "YYYY-MM-DD"), u staršího data (Timestamp) začátek jeho
 * dne v čase zařízení. null = závod bez platného data i uzávěrky
 * (přihlásit se nejde – pravidla přihlášku zamítnou).
 */
export function registrationClosesAtOf(race: DeadlineRace): Date | null {
  const fromDb = toDateSafe(race.registrationClosesAt);
  if (fromDb) return fromDb;
  if (typeof race.date === "string") {
    const m = ISO_DAY.exec(race.date);
    if (m) return pragueMidnight(Number(m[1]), Number(m[2]), Number(m[3]));
  }
  const raceDate = toDateSafe(race.date);
  if (!raceDate) return null;
  const start = new Date(raceDate);
  start.setHours(0, 0, 0, 0);
  return start;
}
